import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiX, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { FaDiscord, FaDollarSign } from 'react-icons/fa';
import { purchaseShopItem } from '../../../../store/shopItems/shopItemsSlice';

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 1200; padding: 20px;
`;

const Box = styled.div`
  background: #161616; border: 1px solid rgba(147,51,234,0.25);
  border-radius: 14px; padding: 24px; max-width: 400px; width: 100%; position: relative;

  h3 { font-size: 16px; font-weight: 800; color: #f1f5f9; margin: 0 0 4px; }
  p { font-size: 12.5px; color: #9ca3af; margin: 0 0 18px; line-height: 1.6; }

  .close-btn {
    position: absolute; top: 16px; inset-inline-end: 16px;
    background: none; border: none; color: #6b7280; cursor: pointer;
    font-size: 16px; display: flex;
    &:hover { color: #e2e8f0; }
  }
`;

const ItemRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  background: #111; border: 1px solid rgba(255,255,255,0.08); border-radius: 9px;
  padding: 12px 14px; margin-bottom: 18px;

  .name { font-size: 13.5px; font-weight: 700; color: #e2e8f0; }
  .price { display: flex; align-items: center; gap: 4px; font-size: 15px; font-weight: 800; color: #e8c05a; }
`;

const AlertBox = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 18px;
  svg { color: #f87171; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fca5a5; font-weight: 600; }
`;

const Actions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end;
  button {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 18px; border-radius: 9px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit; border: none; transition: opacity 0.15s;
  }
  .cancel-btn { background: transparent; color: #9ca3af; border: 1px solid rgba(255,255,255,0.1); }
  .cancel-btn:hover { border-color: rgba(255,255,255,0.2); color: #e2e8f0; }
  .primary-btn { background: #7C3AED; color: #fff; }
  .primary-btn:hover { opacity: 0.85; }
  .primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .discord-btn { background: #5865f2; color: #fff; width: 100%; justify-content: center; }
  .discord-btn:hover { opacity: 0.85; }
`;

const SuccessBox = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 12px 0 20px; text-align: center;
  svg { font-size: 46px; color: #34d399; }
  .title { font-size: 16px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 13px; color: #6b7280; max-width: 300px; }
`;

const BuyModal = ({ item, discordInvite, onClose, onPurchased }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState('confirm');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [shortfall, setShortfall] = useState(0);

  useEffect(() => {
    if (step === 'success') onPurchased?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleConfirm = () => {
    setSubmitting(true);
    setErrorMsg('');
    dispatch(purchaseShopItem({ _id: item._id }))
      .unwrap()
      .then(() => setStep('success'))
      .catch((err) => {
        const first = err?.errors?.[0];
        if (first?.code === 'INSUFFICIENT_BALANCE') {
          setShortfall(Math.max(0, (first.required || 0) - (first.balance || 0)));
          setStep('insufficient');
        } else {
          setErrorMsg(first?.msg || 'something went wrong');
        }
      })
      .finally(() => setSubmitting(false));
  };

  const goTopUp = () => {
    onClose();
    navigate('/wallet', { state: { prefillAmount: shortfall } });
  };

  return (
    <Overlay onClick={onClose}>
      <Box onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose} aria-label="close"><FiX /></button>

        {step === 'confirm' && (
          <>
            <h3>{t('website.buy_modal.title')}</h3>
            <p>{t('website.buy_modal.confirm_body')}</p>
            <ItemRow>
              <span className="name">{item.name}</span>
              <span className="price"><FaDollarSign /> {item.price_num}</span>
            </ItemRow>
            {errorMsg && (
              <AlertBox><FiAlertCircle /><div className="msg">{errorMsg}</div></AlertBox>
            )}
            <Actions>
              <button className="cancel-btn" onClick={onClose}>{t('website.buy_modal.cancel_btn')}</button>
              <button className="primary-btn" disabled={submitting} onClick={handleConfirm}>
                {t('website.buy_modal.confirm_btn')}
              </button>
            </Actions>
          </>
        )}

        {step === 'insufficient' && (
          <>
            <h3>{t('website.buy_modal.title')}</h3>
            <AlertBox><FiAlertCircle /><div className="msg">{t('website.buy_modal.insufficient_balance_msg')}</div></AlertBox>
            <Actions>
              <button className="cancel-btn" onClick={onClose}>{t('website.buy_modal.cancel_btn')}</button>
              <button className="primary-btn" onClick={goTopUp}>{t('website.buy_modal.top_up_now_btn')}</button>
            </Actions>
          </>
        )}

        {step === 'success' && (
          <>
            <SuccessBox>
              <FiCheckCircle />
              <div className="title">{t('website.buy_modal.success_title')}</div>
              <div className="sub">{t('website.buy_modal.success_sub')}</div>
            </SuccessBox>
            <Actions style={{ flexDirection: 'column' }}>
              <a className="discord-btn" style={{ textDecoration: 'none', display: 'flex' }}
                href={discordInvite} target="_blank" rel="noreferrer">
                <FaDiscord /> {t('website.buy_modal.receive_ticket_btn')}
              </a>
              <button className="cancel-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={onClose}>
                {t('website.buy_modal.close_btn')}
              </button>
            </Actions>
          </>
        )}
      </Box>
    </Overlay>
  );
};

export default BuyModal;
